import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';

const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth(); // Renommé pour éviter les conflits
  const [loading, setLoading] = useState(false); // State local pour gérer le chargement
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);

  // Vérifier si nous sommes dans un contexte de réinitialisation de mot de passe valide
  useEffect(() => {
    const checkSession = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Erreur de session:', error);
          setIsValidSession(false);
          return;
        }

        // Si nous avons une session et que l'utilisateur est en mode de récupération
        if (data.session && data.session.user) {
          setIsValidSession(true);
        } else {
          setIsValidSession(false);
          navigate('/auth/login');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de la session:', error);
        setIsValidSession(false);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!password) {
      newErrors.password = t('validation.passwordRequired');
    } else if (password.length < 8) {
      newErrors.password = t('validation.passwordTooShort');
    } else if (!/(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])/.test(password)) {
      newErrors.password = t('auth.passwordRequirements');
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        setLoading(true);
        // Utiliser directement l'API Supabase pour mettre à jour le mot de passe
        const { error } = await supabase.auth.updateUser({ 
          password: password 
        });
        
        if (error) {
          console.error('Erreur lors de la mise à jour du mot de passe:', error);
          setErrors({ general: t('auth.resetPasswordFailed') });
          return;
        }

        setIsSubmitted(true);
        
        // Rediriger vers la page des paramètres après 3 secondes
        setTimeout(() => {
          navigate('/settings');
        }, 3000);
      } catch (error) {
        console.error('Erreur:', error);
        setErrors({ general: t('auth.resetPasswordFailed') });
      } finally {
        setLoading(false);
      }
    }
  };

  // Utiliser loading local au lieu de authLoading
  if (loading) {
    return (
      <div className="bg-gris-clair py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="bg-gris-clair py-8 px-4 shadow sm:rounded-lg sm:px-10">
      {!isValidSession ? (
        <div className="text-center py-4">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="mt-3 text-lg font-medium text-gris-fonce">
            {t('auth.invalidResetLink')}
          </h3>
          <p className="mt-2 text-sm text-gris-fonce/70">
            {t('auth.tryAgain')}
          </p>
          <button
            onClick={() => navigate('/auth/forgot-password')}
            className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-bleu1 hover:bg-bleu3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bleu1"
          >
            {t('auth.requestNewLink')}
          </button>
        </div>
      ) : isSubmitted ? (
        <div className="text-center py-4">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="mt-3 text-lg font-medium text-gris-fonce">
            {t('profile.passwordUpdated')}
          </h3>
          <p className="mt-2 text-sm text-gris-fonce/70">
            {t('common.redirecting')}
          </p>
        </div>
      ) : (
        <>
          <h2 className="mt-2 mb-6 text-center text-xl font-bold text-gris-fonce">
            {t('auth.resetPassword')}
          </h2>
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{errors.general}</p>
              </div>
            )}
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gris-fonce">
                {t('auth.password')}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors({ ...errors, password: '' });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-bleu1`}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
                <p className="mt-1 text-xs text-gris-fonce/70">
                  {t('auth.passwordRequirements')}
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gris-fonce">
                {t('auth.confirmPassword')}
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) {
                      setErrors({ ...errors, confirmPassword: '' });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-bleu1`}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-bleu1 hover:bg-bleu3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bleu1"
              >
                {loading ? t('common.loading') : t('common.save')}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ResetPasswordForm;
